import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DateService } from '../date/date.service';
import { FlightBooking } from '@pages/common-order/_interfaces_/flight-booking.interface';
import { Airport } from '../flight/_interfaces_/leg.interface';
import { DateFormatEnum } from '@enums/date-format.enum';
import { Content, ContentColumns } from 'pdfmake/interfaces';

@Injectable({
  providedIn: 'root',
})
export class FlightDetailsPdfService {
  constructor(private translateService: TranslateService, private dateService: DateService) {}

  public getContent(flightBookings: FlightBooking[]): Content[] {
    const content: Content[] = [{ text: this.translateService.instant('PRINT.PRODUCT_OVERVIEW'), style: 'subheader' }];

    flightBookings?.forEach((flightBooking) => {
      if (flightBooking.pnr) {
        content.push({ text: `${this.translateService.instant('PRINT.PNR')} ${flightBooking.pnr}\n`, style: 'sectionheader' });
      }

      flightBooking?.flightDetails?.legs?.forEach((leg, index) => {
        const direction =
          index === 0
            ? `${this.translateService.instant('DETAILS.FLIGHT_DIRECTION_OUTBOUND')}`
            : `${this.translateService.instant('DETAILS.FLIGHT_DIRECTION_INBOUND')}`;
        const baggage = this.translateService.instant(leg.baggageIncluded ? 'PRINT.BAGGAGE_INCLUDED' : 'PRINT.BAGGAGE_EXCLUDED');
        const segmentRows = this.getSegmentRowsContent(leg);

        content.push(
          {
            table: {
              headerRows: 1,
              widths: ['*', '*'],
              body: [
                [
                  { text: direction, style: 'tableHeader', border: [false, false, false, true] },
                  { text: baggage, style: 'tableHeader', alignment: 'right', border: [false, false, false, true] },
                ],
              ],
            },
          },
          { text: '\n' },
          segmentRows,
          { text: '\n\n\n' }
        );
      });
    });

    return content;
  }

  private getSegmentRowsContent(leg): Content[] {
    const airline = leg.airline;
    const segmentRows = [];

    leg.segments?.forEach((segment, index) => {
      if (segment.hasAirportChanged) {
        segmentRows.push(...this.getAirportChangedColumns(segment.segmentDepartureData?.airport));
      }

      segmentRows.push(this.getSegmentFirstRowColumns(airline, segment), this.getSegmentSecondRowColumns(airline, segment));

      if (segment.waitingTime && index < leg.segments.length - 1) {
        segmentRows.push(...this.getWaitingTimeColumn(segment.waitingTime));
      }
    });

    return segmentRows;
  }

  private getAirportChangedColumns(airport?: Airport): Content[] {
    const columns = [
      {
        text: `${this.translateService.instant('PRINT.AIRPORT_CHANGE')} ${airport?.displayName || '-'}`,
      },
    ];
    return [{ columns }, { text: '\n' }];
  }

  private getSegmentFirstRowColumns(airline, segment): ContentColumns {
    return this.getFlightSegmentColumns([
      segment.segmentDepartureData.displayTime || '-',
      `${segment.segmentDepartureData.airport?.code || '-'} (${segment.segmentDepartureData.airport?.code || '-'})`,
      '>',
      segment.segmentArrivalData.displayTime || '-',
      `${segment.segmentArrivalData.airport?.code || '-'} (${segment.segmentArrivalData.airport?.code || '-'})`,
      segment.cabinClass || '-',
      airline?.displayName || '-',
    ]);
  }

  private getSegmentSecondRowColumns(airline, segment): ContentColumns {
    return this.getFlightSegmentColumns([
      segment.segmentDepartureData.displayDate
        ? this.dateService.formatDateTime(segment.segmentDepartureData.displayDate, DateFormatEnum.ddMMM)
        : '-',
      `${segment.segmentDepartureData.airport?.displayName || '-'}`,
      '',
      segment.segmentArrivalData.displayDate
        ? this.dateService.formatDateTime(segment.segmentArrivalData.displayDate, DateFormatEnum.ddMMM)
        : '-',
      segment.segmentArrivalData.airport?.displayName || '-',
      segment.flightNumber || '-',
      segment.codeShareAirline?.code
        ? `${this.translateService.instant('PRINT.OPERATED_BY')} ${segment.codeShareAirline.displayName}`
        : '-',
    ]);
  }

  private getFlightSegmentColumns(cols: string[]): ContentColumns {
    return {
      columns: [
        { width: 50, text: cols[0] },
        { width: 110, text: cols[1] },
        { width: 20, text: cols[2] },
        { width: 50, text: cols[3] },
        { width: 110, text: cols[4] },
        { width: 90, text: cols[5] },
        { width: 70, text: cols[6] },
      ],
    };
  }

  private getWaitingTimeColumn(waitingTime: string): Content[] {
    const row = [
      {
        text: `${this.dateService.formatTime(waitingTime)} ${this.translateService.instant('COMMON_ORDER.FLIGHT_OVERVIEW.OVERLAY')}`,
      },
    ];
    return [{ text: '\n' }, { columns: row }, { text: '\n' }];
  }
}
